import React, { useState } from 'react';
import { Paper, Title, Text, Group, Button, Stack, Divider, Tooltip, Collapse, Anchor } from '@mantine/core';
import { IconEdit, IconBuilding, IconInfoCircle, IconChevronDown, IconChevronUp, IconExternalLink } from '@tabler/icons-react';
import BusinessInfoForm from '../forms/BusinessInfoForm';
import { useSalon } from '../../context/SalonContext';
import { useMediaQuery } from '@mantine/hooks';
import styles from './BusinessInfoSettings.module.css';
function BusinessInfoSettings({ editingSection, setEditingSection, handleUpdate }) {
  const { businessInfo, currentUser } = useSalon();
  const [expandedAdvancedSettings, setExpandedAdvancedSettings] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Paper p={{ base: 'sm', sm: 0 }} radius="md" withBorder={isMobile}>
      <Group position="apart" mb="md">
        <Group>
          <IconBuilding size={isMobile ? 16 : 24} />
          <Title order={3} size={isMobile ? 'lg' : 'xl'}>Business Information</Title>
        </Group>
        {(currentUser?.role === "Owner" || currentUser?.role === "Manager") && (
          <Button 
            variant="light" 
            color={editingSection === 'info' ? "red" : "violet"}
            onClick={() => editingSection === 'info' ? setEditingSection(null) : setEditingSection('info')}
            ml="auto"
            leftSection={<IconEdit size={16} />}
          >
            {editingSection === 'info' ? 'Cancel Edit' : 'Edit'}
          </Button>
        )}
      </Group>

      {editingSection === 'info' ? (
        <BusinessInfoForm 
          initialData={businessInfo}
          onSubmit={handleUpdate}
          isEditing={true}
          showAdvancedSettings
        />
      ) : (
        <Stack>
          <Stack gap="0">
            <Text fw={600}>Business Name</Text>
            <Text>{businessInfo?.salon_name}</Text>
          </Stack>
          <Stack gap="0">
            <Text fw={600}>Phone</Text>
            <Text>({businessInfo?.area_code}) {businessInfo?.phone_number.slice(0,3)}-{businessInfo?.phone_number.slice(3)}</Text>
          </Stack>
          <Stack gap="0">
            <Text fw={600}>Address</Text>
            <Text>{businessInfo?.street_address.split('|')[0]}</Text>
            <Text>{businessInfo?.street_address.split('|')[1]}</Text>
            <Text>
              {businessInfo?.city}, {businessInfo?.state} {businessInfo?.zip_code}
            </Text>
          </Stack>
          <Stack gap="0">
            <Text fw={600}>Timezone</Text>
            <Text>{businessInfo?.timezone}</Text>
          </Stack>

          <Stack gap="0">
            <Group gap="xs">
              <Text fw={600}>Google Review Link</Text>
              <Tooltip label="Link to your Google Business profile where customers can leave reviews">
                <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
              </Tooltip>
            </Group>
            { businessInfo?.google_review_link ? (
              <Anchor 
                href={businessInfo?.google_review_link} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {businessInfo?.google_review_link}
                <IconExternalLink size={14} />
              </Anchor>
            ) : (
              <Text>N/A</Text>
            )}
          </Stack>

          <Group onClick={() => setExpandedAdvancedSettings(!expandedAdvancedSettings)} className={styles.hoverable} w="fit-content" mt="xs">
            <Title order={4} size={ isMobile ? "sm" : "md"} fw={500}>Advanced Settings</Title>
            {expandedAdvancedSettings ? 
              <IconChevronUp size={16} /> : 
              <IconChevronDown size={16} />
            }
          </Group>

          <Collapse in={expandedAdvancedSettings}>
            <Stack gap="md">
              <Stack gap="0">
                <Group gap="xs">
                  <Text fw={600}>Booking Delay</Text>
                  <Tooltip label="Sets minimum buffer time between now and when a customer can book their next appointment">
                    <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
                  </Tooltip>
                </Group>
                <Text>{businessInfo?.booking_delay_mins} minutes</Text>
              </Stack>
              <Stack gap="0">
                <Group gap="xs">
                  <Text fw={600}>Customer Booking Horizon</Text>
                  <Tooltip label="Sets how far in advance customers can book appointments">
                    <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
                  </Tooltip>
                </Group>
                <Text>{businessInfo?.customer_booking_horizon_days ? `${businessInfo.customer_booking_horizon_days} days` : 'Not set'}</Text>
              </Stack>
              <Stack gap="0">
                <Group gap="xs">
                  <Text fw={600}>Appointments Require Staff Approval</Text>
                  <Tooltip label="Requires staff approval for new self-service bookings before blocking the time in your calendar">
                    <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
                  </Tooltip>
                </Group>
                <Text>{businessInfo?.appts_need_accepting ? 'Yes' : 'No'}</Text>
              </Stack>
              <Stack gap="0">
                <Group gap="xs">
                  <Text fw={600}>Request Customer Appointment Confirmation</Text>
                  <Tooltip label="Sends confirmation requests to customers a few days before their scheduled appointment">
                    <IconInfoCircle size="1rem" style={{ cursor: 'help' }} color="gray" />
                  </Tooltip>
                </Group>
                <Text>{businessInfo?.appts_need_confirmation ? 'Yes' : 'No'}</Text>
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      )}
    </Paper>
  );
}

export default BusinessInfoSettings; 